import { DollarSign, TrendingUp } from 'lucide-react';
import React from 'react';
import {
  useGetOrgDashboardStatsQuery,
  useGetProjectDashboardStatsQuery,
} from '../../services/dashboardApi';
import { WidgetProps } from './types';

const TotalsWidget: React.FC<WidgetProps> = ({ scope, projectId }) => {
  // Use RTK Query hooks based on scope
  const {
    data: orgStats,
    isLoading: isOrgLoading,
    error: orgError,
  } = useGetOrgDashboardStatsQuery(undefined, { skip: scope !== 'org' });

  const {
    data: projectStats,
    isLoading: isProjectLoading,
    error: projectError,
  } = useGetProjectDashboardStatsQuery(
    { projectId: projectId! },
    { skip: scope !== 'project' || !projectId }
  );

  // Determine which data to use
  const isLoading = scope === 'org' ? isOrgLoading : isProjectLoading;
  const error = scope === 'org' ? orgError : projectError;
  const stats = scope === 'org' ? orgStats : projectStats;

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  if (isLoading) {
    return (
      <div className="space-y-6 animate-pulse">
        {[1, 2, 3].map(i => (
          <div key={i} className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-gray-200 rounded-xl"></div>
              <div>
                <div className="h-3 bg-gray-200 rounded w-20 mb-2"></div>
                <div className="h-5 bg-gray-200 rounded w-24"></div>
              </div>
            </div>
            <div className="w-12 h-4 bg-gray-200 rounded"></div>
          </div>
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="text-center">
          <p className="text-red-600 text-sm font-medium">Failed to load financial data</p>
          <p className="text-gray-500 text-xs mt-1">Please try refreshing the widget</p>
        </div>
      </div>
    );
  }

  // Extract values based on scope and data structure
  const revenue =
    scope === 'org' ? (stats as any)?.totalRevenue || 0 : (stats as any)?.projectValue || 0;

  const costs =
    scope === 'org' ? (stats as any)?.totalCosts || 0 : (stats as any)?.spentToDate || 0;

  const profit = revenue - costs;

  return (
    <div className="space-y-6">
      {/* Revenue */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 bg-green-50 rounded-xl flex items-center justify-center ring-1 ring-green-100">
            <DollarSign className="h-5 w-5 text-green-600" />
          </div>
          <div>
            <p className="text-xs text-gray-500 font-medium">
              {scope === 'org' ? 'Total Revenue' : 'Project Value'}
            </p>
            <p className="text-lg font-bold text-gray-900">{formatCurrency(revenue)}</p>
          </div>
        </div>
        <div className="flex items-center space-x-1 text-green-600 bg-green-50 px-2 py-1 rounded-lg">
          <TrendingUp className="h-3 w-3" />
          <span className="text-xs font-semibold">
            {scope === 'org' ? '+8.5%' : `${Math.round((revenue / (costs || 1)) * 100)}%`}
          </span>
        </div>
      </div>

      {/* Costs */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 bg-red-50 rounded-xl flex items-center justify-center ring-1 ring-red-100">
            <DollarSign className="h-5 w-5 text-red-600" />
          </div>
          <div>
            <p className="text-xs text-gray-500 font-medium">
              {scope === 'org' ? 'Total Costs' : 'Spent to Date'}
            </p>
            <p className="text-lg font-bold text-gray-900">{formatCurrency(costs)}</p>
          </div>
        </div>
      </div>

      {/* Profit */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 bg-blue-50 rounded-xl flex items-center justify-center ring-1 ring-blue-100">
            <DollarSign className="h-5 w-5 text-blue-600" />
          </div>
          <div>
            <p className="text-xs text-gray-500 font-medium">
              {scope === 'org' ? 'Net Profit' : 'Remaining Budget'}
            </p>
            <p className={`text-lg font-bold ${profit >= 0 ? 'text-green-700' : 'text-red-700'}`}>
              {formatCurrency(profit)}
            </p>
          </div>
        </div>
      </div>

      {/* Scope indicator */}
      <div className="pt-3 border-t border-gray-200">
        <p className="text-xs text-gray-500 font-medium">
          {scope === 'org' ? 'All Projects' : `Project ${projectId?.slice(-4)}`}
        </p>
      </div>
    </div>
  );
};

export default TotalsWidget;
